import { type FC, type PropsWithChildren, Component, useCallback, useMemo, useState } from "react";
import { DDSDialog, DDSDialogContent, DDSDialogHeader, DDSLogo, SemanticTypography } from "@web-monorepo/dds";
import { UserCancelledCodeEntryError } from "../errors";
import { LOGGING_CONTEXT, useEventLogger } from "../logging";
import VerificationPage, { type Props as ViewProps } from "../VerificationPage";
import CONTEXT, { type GetCodeParams } from "./context";
import styles from "../VerificationPage/VerificationPage.module.css";

let pageKey = 0;
type ProviderProps = PropsWithChildren<{
  entityType: "parent" | "teacher";
}>;

class StifleRejectionErrors extends Component<PropsWithChildren> {
  static getDerivedStateFromError() {
    return null;
  }

  render() {
    return <>{this.props.children}</>;
  }

  componentDidCatch(error: Error): void {
    if (!(error instanceof UserCancelledCodeEntryError)) {
      throw error;
    }
  }
}

export const OTCPageProvider: FC<ProviderProps> = ({ children, entityType }) => {
  const [otcPageProps, setOTCPageProps] = useState<ViewProps | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [_isFullscreen, setFullscreen] = useState(true);
  const isFullscreen = true;
  const logEvent = useEventLogger(entityType);

  const handlers = useMemo(() => {
    const close = () => {
      setOTCPageProps(null);
      setIsDialogOpen(false);
    };

    return {
      getCode: (params: GetCodeParams) =>
        new Promise<string | null>((onCode, onCancel) => {
          pageKey++;
          setOTCPageProps({
            ...params,
            entityType,
            onCode,
            onCancel: () => {
              onCancel(new UserCancelledCodeEntryError(params.error));
              close();
            },
          });
          if (!isDialogOpen) {
            setIsDialogOpen(true);
          }
        }),
      setFullscreen,
      close,
    };
  }, [entityType, isDialogOpen]);

  // Handle dialog close
  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && otcPageProps) {
        logEvent.cancelledOTPEntry({
          productEventNamespace: "one_time_codes",
        });
        otcPageProps.onCancel();
      }
    },
    [logEvent, otcPageProps],
  );

  const navMojo = (
    <nav className={styles.nav}>
      <DDSLogo brand="classdojo" variant="mark" className={styles.mojo} />
    </nav>
  );

  return (
    <StifleRejectionErrors>
      <LOGGING_CONTEXT.Provider value={{ entityType }}>
        <CONTEXT.Provider value={handlers}>
          {children}
          <DDSDialog data-name="one_time_code" open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
            <DDSDialogContent fullscreen={isFullscreen ? true : undefined}>
              <DDSDialogHeader title="One-Time Code" visuallyHideTitle>
                {isFullscreen ? navMojo : null}
              </DDSDialogHeader>
              <SemanticTypography>
                {otcPageProps && <VerificationPage key={pageKey} {...otcPageProps} />}
              </SemanticTypography>
            </DDSDialogContent>
          </DDSDialog>
        </CONTEXT.Provider>
      </LOGGING_CONTEXT.Provider>
    </StifleRejectionErrors>
  );
};
